import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import ListingIcon from './ListingIcon';
import { toVideoEmbedUrl } from '../../utils/videoEmbed';
import type { StoreListing, StoreRelease, StoreReview } from './storeMock';
import '../../styles/MarketplaceStore.css';

/**
 * A Marketplace listing's store page. The same component serves both sides:
 *  - view: what someone browsing the Marketplace sees (Browse → a listing)
 *  - edit: the publish wizard's Store page step, the same page with open slots
 *    for the video, screenshots, description and release notes
 * so a publisher always sees exactly what installers will.
 */

export interface StorePageEditProps {
  videoUrl: string;
  onVideoUrlChange: (url: string) => void;
  screenshots: string[];
  onAddScreenshots: (files: FileList) => void;
  onRemoveScreenshot: (index: number) => void;
  onMoveScreenshot: (index: number, direction: -1 | 1) => void;
  mediaError?: string | null;
  onDescriptionChange: (v: string) => void;
  descriptionHint?: string;
  onDraftWithAi?: () => void;
  drafting?: boolean;
  version: string;
  onVersionChange: (v: string) => void;
  releaseNotes: string;
  /** Editing an existing listing: the version that's live now. */
  lastVersion?: string;
  onReleaseNotesChange: (v: string) => void;
}

export interface StorePageProps {
  mode: 'view' | 'edit';
  name: string;
  /** The listing's emoji, or its type's icon when it has none. */
  emoji: React.ReactNode;
  iconUrl?: string;
  typeKey: string;
  typeLabel: string;
  category: string;
  visibilityLabel: string;
  publisherName: string;
  /** Tags the publisher "Your agency" when it's the viewer's own. */
  isOwnAgency?: boolean;
  /** Makes the publisher a link that shows everything else they've published. */
  onPublisherClick?: () => void;
  /** Chips for where it's added (Digital Workforce, or a widget's places). */
  groups?: React.ReactNode;
  updatedAt?: string;
  description: string;
  listing: StoreListing;
  /** What the listing brings with it — the per-type detail body. */
  includes: React.ReactNode;
  /** Where it lands after install, in plain words. */
  whereItGoes: React.ReactNode;
  /** View mode: the install controls in the header. */
  actions?: React.ReactNode;
  /** View mode, for something the viewer has installed: their install's settings, keys and
   *  agents, right under the header (the Installed tab opens the same store page). */
  manage?: React.ReactNode;
  /** View mode: lets someone who installed it leave a review. */
  canReview?: boolean;
  onSubmitReview?: (rating: number, comment: string) => void;
  edit?: StorePageEditProps;
}

/** "MCP Server" -> "MCP server", "Skill" -> "skill": lowercase for use mid-sentence, acronyms kept. */
export function typeInSentence(label: string): string {
  return label.split(' ').map(w => (w.length > 1 && w === w.toUpperCase() ? w : w.toLowerCase())).join(' ');
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const Stars: React.FC<{ value: number; size?: number }> = ({ value, size = 13 }) => (
  <span className="ms-stars" style={{ fontSize: size }} aria-label={`${value.toFixed(1)} out of 5`}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} className={n <= Math.round(value) ? 'ms-stars__on' : 'ms-stars__off'}>★</span>
    ))}
  </span>
);

const Markdown: React.FC<{ text: string }> = ({ text }) => (
  <div className="ms-markdown">
    <ReactMarkdown
      // Publisher-written Markdown: no raw HTML (escaped by react-markdown), no remote
      // images (they'd leak viewers' IPs), links open in a new tab.
      components={{
        a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
        img: () => null,
      }}
    >
      {text}
    </ReactMarkdown>
  </div>
);

/** Long content (an agent's whole agents.md, a 50-endpoint API) starts folded so the page stays scannable. */
const ClampedBlock: React.FC<{ children: React.ReactNode; maxHeight?: number }> = ({ children, maxHeight = 420 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tall, setTall] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setTall(el.scrollHeight > maxHeight + 40);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxHeight]);
  return (
    <div className={`ms-clamp${tall && !open ? ' ms-clamp--folded' : ''}`}>
      <div ref={ref} className="ms-clamp__body" style={tall && !open ? { maxHeight } : undefined}>{children}</div>
      {tall && (
        <button type="button" className="ms-link-btn" onClick={() => setOpen(o => !o)}>{open ? 'Show less' : 'Show all'}</button>
      )}
    </div>
  );
};

type MediaItem = { kind: 'video-embed'; url: string } | { kind: 'video-sample'; poster: string } | { kind: 'image'; src: string };

const StorePage: React.FC<StorePageProps> = ({
  mode, name, emoji, iconUrl, typeKey, typeLabel, category, visibilityLabel, publisherName, isOwnAgency, onPublisherClick, groups, updatedAt,
  description, listing, includes, whereItGoes, actions, manage, canReview, onSubmitReview, edit,
}) => {
  const editing = mode === 'edit' && !!edit;
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [showAllReleases, setShowAllReleases] = useState(false);
  const [previewDescription, setPreviewDescription] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The video always comes first, whatever order things were added in.
  const media: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [];
    if (editing) {
      const embed = edit!.videoUrl ? toVideoEmbedUrl(edit!.videoUrl) : null;
      if (embed) items.push({ kind: 'video-embed', url: embed });
      edit!.screenshots.forEach(src => items.push({ kind: 'image', src }));
    } else {
      if (listing.video?.kind === 'embed') {
        const embed = toVideoEmbedUrl(listing.video.url);
        if (embed) items.push({ kind: 'video-embed', url: embed });
      } else if (listing.video?.kind === 'sample') {
        items.push({ kind: 'video-sample', poster: listing.video.poster });
      }
      listing.screenshots.forEach(src => items.push({ kind: 'image', src }));
    }
    return items;
  }, [editing, edit, listing]);

  const reviews = listing.reviews;
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  const histogram = [5, 4, 3, 2, 1].map(stars => ({ stars, count: reviews.filter(r => r.rating === stars).length }));
  const releases: StoreRelease[] = listing.releases;
  const latest = releases[0];
  const shownReleases = showAllReleases ? releases : releases.slice(0, 2);
  const videoUrlInvalid = editing && !!edit!.videoUrl.trim() && !toVideoEmbedUrl(edit!.videoUrl);

  const submitReview = () => {
    if (!reviewRating || !onSubmitReview) return;
    onSubmitReview(reviewRating, reviewText.trim());
    setReviewRating(0);
    setReviewText('');
    setReviewOpen(false);
  };

  return (
    <div className={`ms-page${editing ? ' ms-page--edit' : ''}`}>
      {/* ── Header ── */}
      <div className="ms-hero">
        <ListingIcon iconUrl={iconUrl} emoji={emoji} className="ms-hero__icon" alt={`${name} icon`} />
        <div className="ms-hero__info">
          <h1 className="ms-hero__name">{name || 'Untitled listing'}</h1>
          <div className="ms-hero__publisher">
            {onPublisherClick ? (
              <button type="button" className="mt-publisher-link" onClick={onPublisherClick} title={`Show everything from ${publisherName}`}>{publisherName}</button>
            ) : publisherName}
            {isOwnAgency && <span className="mt-publisher-tag">Your agency</span>}
          </div>
          <div className="ms-hero__badges">
            <span className={`mt-badge mt-badge--type mt-badge--type-${typeKey}`}>{typeLabel}</span>
            {category && <span className="mt-badge mt-badge--category">{category}</span>}
            <span className="mt-badge mt-badge--visibility">{visibilityLabel}</span>
          </div>
          {groups}
          <div className="ms-hero__stats">
            <div className="ms-stat">
              <span className="ms-stat__value">{reviews.length ? <>{avg.toFixed(1)} <span className="ms-stat__star">★</span></> : '—'}</span>
              <span className="ms-stat__label">{reviews.length ? `${reviews.length} review${reviews.length === 1 ? '' : 's'}` : 'No reviews yet'}</span>
            </div>
            <div className="ms-stat">
              <span className="ms-stat__value">{editing ? (edit!.version || '1.0.0') : latest?.version ?? '1.0.0'}</span>
              <span className="ms-stat__label">Version</span>
            </div>
            <div className="ms-stat">
              <span className="ms-stat__value">{formatDate(editing ? new Date().toISOString() : latest?.date ?? updatedAt) || '—'}</span>
              <span className="ms-stat__label">Updated</span>
            </div>
          </div>
        </div>
        <div className="ms-hero__actions">
          {editing ? <span className="ms-preview-chip">Preview: this is what installers see</span> : actions}
        </div>
      </div>

      {!editing && manage}

      {/* ── Media: video first, then screenshots ── */}
      {(media.length > 0 || editing) && (
        <div className="ms-media">
          {editing && !media.some(m => m.kind !== 'image') && (
            <div className="ms-media__slot ms-media__slot--video">
              <span className="ms-media__slot-icon">▶</span>
              <span className="ms-media__slot-title">Add a video</span>
              <span className="ms-media__slot-hint">Paste a YouTube, Vimeo or Loom link below. It always shows first.</span>
            </div>
          )}
          {media.map((item, i) => (
            <div key={i} className={`ms-media__item${item.kind === 'image' ? '' : ' ms-media__item--video'}`}>
              {item.kind === 'video-embed' && (
                <iframe src={item.url} title={`${name} video`} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen sandbox="allow-scripts allow-same-origin allow-presentation" />
              )}
              {item.kind === 'video-sample' && (
                <button type="button" className="ms-media__open" onClick={() => setLightbox(item)}>
                  <img src={item.poster} alt={`${name} video`} />
                </button>
              )}
              {item.kind === 'image' && (
                <button type="button" className="ms-media__open" onClick={() => setLightbox(item)}>
                  <img src={item.src} alt={`${name} screenshot ${i + 1}`} loading="lazy" />
                </button>
              )}
              {editing && item.kind === 'image' && (
                <div className="ms-media__controls">
                  {(() => {
                    const shotIndex = edit!.screenshots.indexOf(item.src);
                    return (<>
                      <button type="button" onClick={() => edit!.onMoveScreenshot(shotIndex, -1)} disabled={shotIndex === 0} aria-label="Move left">‹</button>
                      <button type="button" onClick={() => edit!.onMoveScreenshot(shotIndex, 1)} disabled={shotIndex === edit!.screenshots.length - 1} aria-label="Move right">›</button>
                      <button type="button" onClick={() => edit!.onRemoveScreenshot(shotIndex)} aria-label="Remove screenshot">×</button>
                    </>);
                  })()}
                </div>
              )}
            </div>
          ))}
          {editing && (
            <>
              <button type="button" className="ms-media__slot" onClick={() => fileInputRef.current?.click()}>
                <span className="ms-media__slot-icon">＋</span>
                <span className="ms-media__slot-title">Add screenshots</span>
                <span className="ms-media__slot-hint">PNG, JPG or WebP</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden
                onChange={e => { if (e.target.files?.length) edit!.onAddScreenshots(e.target.files); e.target.value = ''; }} />
            </>
          )}
        </div>
      )}
      {editing && (
        <div className="ms-video-input">
          <label className="mt-label" htmlFor="ms-video-url">Video link</label>
          <input id="ms-video-url" className="mt-input" type="url" placeholder="https://www.youtube.com/watch?v=… or a Vimeo / Loom link"
            value={edit!.videoUrl} onChange={e => edit!.onVideoUrlChange(e.target.value)} />
          {videoUrlInvalid && <p className="mt-error-text">That isn't a YouTube, Vimeo or Loom link.</p>}
          {edit!.mediaError && <p className="mt-error-text">{edit!.mediaError}</p>}
        </div>
      )}

      <div className="ms-body">
        <div className="ms-main">
          {/* ── About ── */}
          <section className="ms-section">
            <div className="ms-section__head">
              <h2 className="ms-section__title">About this {typeInSentence(typeLabel)}</h2>
              {editing && (
                <div className="ms-section__tools">
                  <div className="ms-toggle" role="tablist">
                    <button type="button" role="tab" aria-selected={!previewDescription} className={!previewDescription ? 'ms-toggle--on' : ''} onClick={() => setPreviewDescription(false)}>Write</button>
                    <button type="button" role="tab" aria-selected={previewDescription} className={previewDescription ? 'ms-toggle--on' : ''} onClick={() => setPreviewDescription(true)}>Preview</button>
                  </div>
                  {edit!.onDraftWithAi && (
                    <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm ms-ai-btn" onClick={() => { setPreviewDescription(false); edit!.onDraftWithAi!(); }} disabled={edit!.drafting}>
                      {edit!.drafting ? 'Drafting…' : '✨ Draft with AI'}
                    </button>
                  )}
                </div>
              )}
            </div>
            {editing && previewDescription ? (
              description ? <Markdown text={description} /> : <p className="ms-muted">Nothing written yet.</p>
            ) : editing ? (
              <>
                <textarea className="mt-textarea ms-description-input" rows={9} value={description} onChange={e => edit!.onDescriptionChange(e.target.value)}
                  placeholder="What does it do, and who is it for? Markdown works: **bold**, - lists, [links](https://…)" />
                {edit!.descriptionHint && <p className="mt-field-hint">{edit!.descriptionHint}</p>}
              </>
            ) : description ? (
              <Markdown text={description} />
            ) : (
              <p className="ms-muted">The publisher hasn't added a description yet.</p>
            )}
          </section>

          {/* ── What's included ── */}
          <section className="ms-section">
            <h2 className="ms-section__title">What's included</h2>
            {editing && <p className="ms-section__hint">Filled in from your {typeInSentence(typeLabel)} automatically.</p>}
            <ClampedBlock><div className="ms-includes">{includes}</div></ClampedBlock>
          </section>

          {/* ── Ratings & reviews ── */}
          <section className="ms-section">
            <div className="ms-section__head">
              <h2 className="ms-section__title">Ratings and reviews</h2>
              {!editing && canReview && !reviewOpen && (
                <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => setReviewOpen(true)}>Write a review</button>
              )}
            </div>
            {editing ? (
              <p className="ms-muted">Reviews show up here once people install it. Only people who've installed it can leave one.</p>
            ) : (
              <>
                {reviewOpen && (
                  <div className="ms-review-form">
                    <div className="ms-review-form__stars" role="radiogroup" aria-label="Your rating">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button key={n} type="button" role="radio" aria-checked={reviewRating === n} className={n <= reviewRating ? 'ms-stars__on' : 'ms-stars__off'} onClick={() => setReviewRating(n)}>★</button>
                      ))}
                    </div>
                    <textarea className="mt-textarea" rows={3} placeholder="What worked, and what could be better?" value={reviewText} onChange={e => setReviewText(e.target.value)} />
                    <div className="ms-review-form__actions">
                      <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={() => setReviewOpen(false)}>Cancel</button>
                      <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={!reviewRating} onClick={submitReview}>Post review</button>
                    </div>
                  </div>
                )}
                {reviews.length === 0 ? (
                  <p className="ms-muted">No reviews yet.</p>
                ) : (
                  <>
                    <div className="ms-ratings">
                      <div className="ms-ratings__score">
                        <span className="ms-ratings__avg">{avg.toFixed(1)}</span>
                        <Stars value={avg} size={15} />
                        <span className="ms-muted">{reviews.length} review{reviews.length === 1 ? '' : 's'}</span>
                      </div>
                      <div className="ms-ratings__bars">
                        {histogram.map(h => (
                          <div key={h.stars} className="ms-ratings__bar-row">
                            <span>{h.stars}</span>
                            <div className="ms-ratings__bar"><div style={{ width: `${(h.count / reviews.length) * 100}%` }} /></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="ms-reviews">
                      {reviews.slice(0, 4).map((r: StoreReview) => (
                        <div key={r.id} className="ms-review">
                          <div className="ms-review__head">
                            <span className="ms-review__avatar">{r.reviewer.charAt(0)}</span>
                            <div>
                              <div className="ms-review__name">{r.reviewer}</div>
                              <div className="ms-review__agency">{r.agency}</div>
                            </div>
                            <span className="ms-review__date">{formatDate(r.date)}</span>
                          </div>
                          <Stars value={r.rating} />
                          {r.comment && <p className="ms-review__text">{r.comment}</p>}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </section>

          {/* ── Version history ── */}
          <section className="ms-section">
            <h2 className="ms-section__title">{editing ? "What's new in this version" : 'Version history'}</h2>
            {editing ? (
              <div className="ms-release-edit">
                <div className="mt-field">
                  <label className="mt-label" htmlFor="ms-version">Version</label>
                  <input id="ms-version" className="mt-input" type="text" placeholder="1.0.0" value={edit!.version} onChange={e => edit!.onVersionChange(e.target.value)} />
                  {edit!.lastVersion && <span className="mt-field-hint">Live now: v{edit!.lastVersion}</span>}
                </div>
                <div className="mt-field mt-field--grow">
                  <label className="mt-label" htmlFor="ms-notes">What changed?</label>
                  <textarea id="ms-notes" className="mt-textarea" rows={3} placeholder={edit!.lastVersion ? `What's different from v${edit!.lastVersion}? Installers read this to see if it fixes their issue.` : 'First release.'}
                    value={edit!.releaseNotes} onChange={e => edit!.onReleaseNotesChange(e.target.value)} />
                </div>
              </div>
            ) : releases.length === 0 ? (
              <p className="ms-muted">No releases recorded yet.</p>
            ) : (
              <>
                <ol className="ms-releases">
                  {shownReleases.map((rel, i) => (
                    <li key={rel.version + i} className="ms-release">
                      <div className="ms-release__head">
                        <span className="ms-release__version">v{rel.version}</span>
                        {i === 0 && <span className="ms-release__current">Latest</span>}
                        <span className="ms-release__date">{formatDate(rel.date)}</span>
                      </div>
                      {rel.notes && <p className="ms-release__notes">{rel.notes}</p>}
                    </li>
                  ))}
                </ol>
                {releases.length > shownReleases.length && (
                  <button type="button" className="ms-link-btn" onClick={() => setShowAllReleases(true)}>Show all {releases.length} versions</button>
                )}
              </>
            )}
          </section>
        </div>

        {/* ── Side column ── */}
        <aside className="ms-side">
          <div className="ms-card">
            <h3 className="ms-card__title">Where it goes</h3>
            <div className="ms-card__body">{whereItGoes}</div>
          </div>
          <div className="ms-card">
            <h3 className="ms-card__title">Details</h3>
            <dl className="ms-details">
              <dt>Publisher</dt><dd>{publisherName}</dd>
              <dt>Type</dt><dd>{typeLabel}</dd>
              {category && (<><dt>Category</dt><dd>{category}</dd></>)}
              <dt>Version</dt><dd>{editing ? (edit!.version || '1.0.0') : latest?.version ?? '1.0.0'}</dd>
              <dt>Updated</dt><dd>{formatDate(editing ? new Date().toISOString() : latest?.date ?? updatedAt) || '—'}</dd>
              <dt>Available to</dt><dd>{visibilityLabel}</dd>
            </dl>
          </div>
        </aside>
      </div>

      {lightbox && (
        <div className="ms-lightbox" role="dialog" aria-modal="true" onClick={() => setLightbox(null)}>
          <button type="button" className="ms-lightbox__close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
          {lightbox.kind === 'image' && <img src={lightbox.src} alt={`${name} screenshot`} onClick={e => e.stopPropagation()} />}
          {lightbox.kind === 'video-sample' && (
            <div className="ms-lightbox__video" onClick={e => e.stopPropagation()}>
              <img src={lightbox.poster} alt={`${name} video`} />
              <p>Sample video slot. A publisher's YouTube, Vimeo or Loom link plays here.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StorePage;
